import { ref } from "vue";
import { defineStore } from "pinia";

import useMyFetch from "@stores/useMyFetch";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import { captureEvent } from "@/lib/analytics";

export const useContractsStore = defineStore("contracts", () => {
  // state
  const contracts = ref([]);
  const contractsCount = ref(0);
  const currentPage = ref(1);
  const searchQuery = ref("");
  const statusFilter = ref(/** @type {string[]} */ ([])); // ["active"] | ["expired"] | []
  const accountsFilter = ref(/** @type {number[]} */ ([])); // [account_id, ...]
  const ownersFilter = ref(/** @type {number[]} */ ([])); // [user_id, ...]
  // Active contracts for the current survey's account, used by Setup form fields.
  // Kept separate from `contracts` so it doesn't pollute the /contracts list view.
  const setupContracts = ref([]);
  const nextToExpire = ref({ contract: null, position: 0, active_count: 0 });

  // actions
  async function fetch(page = 1) {
    const params = new URLSearchParams();
    params.append("page", page);
    if (searchQuery.value.trim()) params.append("search", searchQuery.value.trim());
    // status filter is single-select but stored as array for FilterDropdown compat
    if (statusFilter.value.length) params.append("status", statusFilter.value[0]);
    accountsFilter.value.forEach((id) => params.append("account_ids[]", id));
    ownersFilter.value.forEach((id) => params.append("owner_ids[]", id));

    const { data, error } = await useMyFetch(`/contracts?${params.toString()}`).json();
    if (error.value) throw error.value;
    contracts.value = data.value?.contracts || [];
    contractsCount.value = data.value?.contracts_count || 0;
    currentPage.value = page;
    return contracts.value;
  }

  function clearFilters() {
    resetSearchQuery();
    statusFilter.value = [];
    accountsFilter.value = [];
    ownersFilter.value = [];
    return fetch(1);
  }

  function resetAllFilters() {
    resetSearchQuery();
    statusFilter.value = [];
    accountsFilter.value = [];
    ownersFilter.value = [];
    currentPage.value = 1;
    contracts.value = [];
    contractsCount.value = 0;
  }

  // Fetches active contracts for a single account into setupContracts (keeps
  // `contracts` reserved for the /contracts list view).
  async function fetchActiveForAccount(accountId) {
    if (!accountId) {
      setupContracts.value = [];
      return setupContracts.value;
    }
    const params = new URLSearchParams();
    params.append("status", "active");
    params.append("account_ids[]", accountId);
    params.append("per_page", 100);

    const { data, error } = await useMyFetch(`/contracts?${params.toString()}`).json();
    if (error.value) throw error.value;
    setupContracts.value = data.value?.contracts || [];
    return setupContracts.value;
  }

  const { resetSearchQuery, setSearchQuery } = useDebouncedSearchQuery(searchQuery, () => fetch(1));

  async function fetchNextToExpire() {
    const { data, error } = await useMyFetch("/contracts/next_to_expire").json();
    if (error.value) throw error.value;
    nextToExpire.value = data.value || { contract: null, position: 0, active_count: 0 };
    return nextToExpire.value;
  }

  async function fetchOne(id) {
    const { data, error } = await useMyFetch(`/contracts/${id}`).json();
    if (error.value) throw error.value;
    const index = contracts.value.findIndex((c) => c.id === id);
    if (index !== -1) contracts.value[index] = data.value;
    else contracts.value.push(data.value);
    return data.value;
  }

  async function createContract(payload) {
    const { data, error } = await useMyFetch("/contracts").post({ contract: payload }).json();
    if (error.value) throw error.value;

    captureEvent("contract_created", { contract_id: data.value?.id });
    contracts.value.push(data.value);
    return data.value;
  }

  async function updateContract(id, payload) {
    const { data, error } = await useMyFetch(`/contracts/${id}`).patch({ contract: payload }).json();
    if (error.value) throw error.value;

    captureEvent("contract_updated", { contract_id: id });
    const index = contracts.value.findIndex((c) => c.id === id);
    if (index !== -1) contracts.value[index] = data.value;
    return data.value;
  }

  async function deleteContract(id) {
    const { error } = await useMyFetch(`/contracts/${id}`).delete();
    if (error.value) throw error.value;

    captureEvent("contract_deleted", { contract_id: id });

    contracts.value = contracts.value.filter((c) => c.id !== id);
    contractsCount.value = Math.max(0, contractsCount.value - 1);
  }

  async function setManualCredits(surveyToken, value) {
    const { data, error } = await useMyFetch(`/surveys/${surveyToken}`)
      .patch({ survey: { credits_consumed_manual: value } })
      .json();
    if (error.value) throw error.value;

    captureEvent("survey_manual_credits_set", { survey_token: surveyToken });
    return data.value;
  }

  return {
    // state
    contracts,
    contractsCount,
    currentPage,
    searchQuery,
    statusFilter,
    accountsFilter,
    ownersFilter,
    setupContracts,
    nextToExpire,
    // actions
    fetch,
    fetchActiveForAccount,
    setSearchQuery,
    clearFilters,
    resetAllFilters,
    fetchNextToExpire,
    fetchOne,
    createContract,
    updateContract,
    deleteContract,
    setManualCredits,
  };
});
